import { useQuery } from "@apollo/client";
import useMapViewConfiguration from "contexts/MapViewConfigurationContext/hooks/useMapViewConfiguration";
import { useBusesWithCapacityCostsVariables } from "contexts/MapViewDataContext/hooks/useBusData";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import {
    GetFavoriteBusesWithCapacityCostQuery,
    GetFavoriteBusesWithCapacityCostQueryVariables
} from "generated/graphql";
import { loader } from "graphql.macro";
import { maybeHideLockedBusesFilter } from "graphql/helpers/busHelpers";
import { getCapacityCostsWhereClauseWithScopeView } from "graphql/helpers/capacityCostsWhereClause";
import { useMemo } from "react";
import {
    convertHasuraBusesToNiraBusesAndSplitBasedOnAuth,
    NiraBusId,
    ScopedBus
} from "types/busType";
import { keepSavedFavorites } from "./keepSavedFavorites";

const GET_FAVORITE_BUSES_WITH_CAPACITY_COST = loader(
    "src/graphql/getFavoriteBusesWithCapacityCost.graphql"
);

/**
 * Most favorites, per region, that the panel will resolve in one fetch.
 *
 * This query runs `no-cache` and re-fires whenever a bus filter changes, with
 * an `_in` list as long as the user's favorites, so an unbounded list makes
 * ordinary filter tweaks sluggish. Favorites arrive newest-first, so the cap
 * keeps the most recently starred. Well above any realistic list -- the largest
 * comparable per-user collection in prod is far smaller -- but it stops one
 * enthusiastic user from degrading their own map.
 */
export const FAVORITES_FETCH_LIMIT = 200;

export type FavoriteScopedBusesResult = {
    readonly scopedBuses: ReadonlyArray<ScopedBus>;
    // True while a fetch for the current ids is in flight. The panel uses it
    // to hold back "no results" copy that would otherwise flash after every
    // star, since scopedBuses is previousData until the fetch lands.
    readonly isLoading: boolean;
};

// Favorites the user has saved, with live cost and capacity data. The results
// list is bounded by the viewport and the filters, so a favorite outside it is
// not in useMapViewData and has to be fetched by id.
const useFavoriteScopedBuses = (
    niraBusIds: ReadonlyArray<NiraBusId>
): FavoriteScopedBusesResult => {
    const { region, scopeView } = useMapViewRoutingMetadata();
    const {
        busFiltersConfiguration: { busFilters }
    } = useMapViewConfiguration();
    const {
        includeConstraintCount,
        constraint_stacks_where_clause,
        capacity_costs_where_clause: cappedCapacityCostsWhereClause
    } = useBusesWithCapacityCostsVariables();

    const capacityCostsWhereClause = getCapacityCostsWhereClauseWithScopeView({
        scope: busFilters.scope,
        scopeView,
        maybeMaxAllocatedCosts: undefined,
        maybeMaxTotalCosts: undefined,
        includePreExistingConstraints: busFilters.includePreExistingConstraints
    });

    const boundedNiraBusIds = niraBusIds.slice(0, FAVORITES_FETCH_LIMIT);

    const queryResult = useQuery<
        GetFavoriteBusesWithCapacityCostQuery,
        GetFavoriteBusesWithCapacityCostQueryVariables
    >(GET_FAVORITE_BUSES_WITH_CAPACITY_COST, {
        variables: {
            niraBusIds: [...boundedNiraBusIds],
            limit: FAVORITES_FETCH_LIMIT,
            // Fetch favorite buses with two sets of capacity costs data:
            // 1. Without cost bounds, so we fetch for all favorites even if they are
            // not within filters
            // 2. With cost bounds, so we can correctly show data for favorites
            // that are within user cost filters
            capacity_costs_where_clause: capacityCostsWhereClause,
            capped_capacity_costs_where_clause: cappedCapacityCostsWhereClause,
            region,
            scope: busFilters.scope,
            scopeView,
            // The panel ignores Hide locked buses.
            filter_locked_buses_where_clause: maybeHideLockedBusesFilter(false),
            includeConstraintCount,
            constraint_stacks_where_clause
        },
        skip: boundedNiraBusIds.length === 0,
        fetchPolicy: "no-cache"
    });

    // Fall back to previousData so the panel does not empty out while a
    // refetch is in flight.
    const buses = queryResult.data?.buses ?? queryResult.previousData?.buses;

    // `loading` covers the in-flight fetch. The second clause covers the render
    // in which the ids change and no result for them exists yet, so the two
    // never disagree about whether the rows match the ids. A skipped query
    // (no ids) and an errored one are not loading.
    const isLoading =
        queryResult.loading ||
        (boundedNiraBusIds.length > 0 &&
            queryResult.data === undefined &&
            queryResult.error === undefined);

    const scopedBuses = useMemo(
        () =>
            buses === undefined
                ? []
                : keepSavedFavorites(toFavoriteScopedBuses(buses), niraBusIds),
        [buses, niraBusIds]
    );

    return useMemo(
        () => ({ scopedBuses, isLoading }),
        [scopedBuses, isLoading]
    );
};
// For favorite buses that have capacity cost data within the user's cost filters, use that.
// Otherwise, it will default to showing the maximum studied size.
const toFavoriteScopedBuses = (
    buses: GetFavoriteBusesWithCapacityCostQuery["buses"]
): ReadonlyArray<ScopedBus> => {
    const busesWithCappedCosts = buses.map((hasuraBus) =>
        hasuraBus.capped_capacity_costs.length > 0
            ? { ...hasuraBus, capacity_costs: hasuraBus.capped_capacity_costs }
            : hasuraBus
    );
    const [scopedBuses] =
        convertHasuraBusesToNiraBusesAndSplitBasedOnAuth(busesWithCappedCosts);
    return scopedBuses;
};

export default useFavoriteScopedBuses;
